import { LEVEL_STARS } from 'src/components/BirdieRoundCard/birdieCardModel'
import { selectRoundLocation, selectRoundOdds } from 'src/components/BirdieRoundCard/roundOdds'
import type { Marble } from 'src/types/domain/Marble'
import type { Race } from 'src/types/domain/Race'
import { isRaceSettledEvent, type RaceSettledEvent } from 'src/types/domain/RaceEvent'
import { puttResultVisible, BIRDIE_RESULT_HOLD_MS } from './boardPhase'
import { golferInitials } from './capsuleState'
import {
  circlesFor,
  countWinnerId,
  exactWinnerId,
  patternFromSettled,
  roundTypeFromSettled,
  type SettledRoundType
} from './settlement'
import type { PuttMark } from './tileViewModel'

/**
 * The past-rounds strip's data (B17).
 *
 * Owner, 2026-09-08: "the past rounds we show to the right could still be shown
 * but should be showing the golfer/value of that round instead … showing the
 * past results does not make much sense as it changes each time with the
 * probability so a hard past bet could be a easy bet on the next round …
 * players would expect to see some sort of past game stats".
 *
 * So a Birdie row is NOT "which option won" — that number means nothing across
 * rounds when the board is repriced every round. It is the round's DIFFICULTY
 * (the golfer and their make chance), its SCORE (the three putts) and WHAT IT
 * PAID (the count price). Read together those three do compare across rounds.
 *
 * Everything here is derived from events the widget already holds; nothing in
 * this module fetches, and nothing in it is allowed to know about React.
 */

/** The public, open-source verifier. Same host the crash round modal opens
 *  (`CrashRoundModal.tsx:352`) — one deployment, every game family, no env
 *  switching (it is a GitHub Pages site, not a per-environment service). */
export const VERIFIER_BASE_URL = 'https://marbles-io.github.io/155-fairness-verifier/'

/** Rows the strip keeps. Beyond this the list is scrolled, not grown. */
export const BIRDIE_PAST_ROUNDS_MAX = 8

export interface BirdiePastGolfer {
  name: string
  headshotUrl?: string
  /** Up to two letters for the neutral disc when there is no headshot. */
  initials: string
}

/**
 * The round's provably-fair reveal, as the Birdie engine actually publishes it.
 *
 * Field-by-field from `engine/birdie/fairness.py:build_birdie_reveal` (which
 * wraps `engine/fairness_runtime.py:build_marble_fairness_reveal`). The wire
 * keys are declared on `RaceSettledEvent['payload']['fairness']` alongside the
 * crash, counting and marble families, so the reader below is spelled against
 * that type and a mistyped key is a compile error rather than a silently
 * missing row. The payload survives the store round-trip verbatim
 * (`RaceEventMap.ts:43,222` cast it, they do not rebuild it), so every key the
 * engine writes is present at runtime.
 *
 * The declaration is still not a guarantee: it describes what the engine MEANS
 * to send, and the settled-event guard does not validate `fairness` at all
 * (see `readBirdieFairness`). Every read stays behind a runtime guard.
 *
 * Every field is optional on purpose: a round settled by an older engine, or a
 * reveal that never landed, must render the part it has rather than nothing.
 */
export interface BirdieRoundFairness {
  /** `"birdie"`, the family discriminator the verifier dispatches on. */
  game?: string
  /** The pre-round commitment — preferred from `race_ready`, which published it
   *  BEFORE betting opened; the reveal's own copy is the fallback. */
  serverSeedHash?: string
  serverSeed?: string
  chainRootHash?: string
  chainIndex?: number
  /** The single "order" draw that picked the pattern. */
  draw?: string
  marbles?: string[]
  order?: string[]
  k?: number
  index?: number
  weightsPpm?: number[]
  /** The card derivation: its own domain-separated draw over the same seed. */
  cardDraw?: string
  cardIndex?: number
  catalogueVersion?: number
  catalogueHash?: string
  catalogueSize?: number
  makeRatePpm?: number
  /** The base RTP the board was priced at — the reveal's own `rtp_ppm`, else the odds snapshot's. */
  rtpPpm?: number
  paytableHash?: string
  clipId?: string
  clipSha256?: string
  /** The round-type side market: the drawn type, its own draw, and the weights
   *  it was drawn against (name → ppm) — the verifier needs all three. */
  roundType?: string
  roundTypeDraw?: string
  roundTypesPpm?: Record<string, number>
}

/**
 * One option the round's Lightning boost was on, priced BOTH ways (B19).
 *
 * The boost is not on the odds snapshot — `race_ready.payload.odds` carries the
 * committed base multipliers and nothing else. It is on the RACE ROW:
 * `is_lightning_round` / `lightning_boost_percent` / `lightning_boosted_items`
 * (marble ids), mapped straight through by `RaceMap` and `NormalizedRaceMap`,
 * so a settled round out of history carries it exactly as the live board does
 * (`BirdieBoard.tsx:283-296` reads the same three fields for the live tiles).
 *
 * `paid` is computed the same way the live tile computes it — base times
 * `1 + percent/100` (`tileViewModel.buildTileViews`) — so the card's struck
 * base and boosted price can never disagree with the board the player bet on.
 */
export interface BirdieRoundBoost {
  /** The canonical option key, e.g. `HMH` or `IN2`. */
  key: string
  /** That round's own committed price for the option, UNBOOSTED. */
  base: number
  /** …and the boosted price it actually paid. */
  paid: number
}

export interface BirdiePastRound {
  roundId: string
  roundNumber?: number
  golfer: BirdiePastGolfer
  /** The round's make chance per putt, as whole percent. */
  makeRatePct: number
  /** The golfer's age, straight off the committed card. */
  age?: number
  /** …and their handicap. Optional on the wire, so optional here. */
  handicap?: number
  distanceM?: number
  /** 0..5 — 0 for a level the widget does not know (draws an empty bar). */
  levelStars: number
  /** 1..5 straight off the card. */
  difficultyStars: number
  /** The three putts in putt order. Empty when the pattern is unreadable. */
  marks: PuttMark[]
  /** `HMH` — the winning EXACT option. */
  patternKey?: string
  /** `IN2` — the winning COUNT option, for the "what paid" label. */
  countKey?: string
  /** The count price that paid, unrounded (format at the edge). */
  countPaid?: number
  /**
   * B36: the round's TYPE as settled — 'FROST', 'FIRE' or 'PLAIN'. Undefined on a round that carried
   * no round-type market, which is every round before the side bets existed; a past-rounds rail spans
   * both, so the field has to be able to say "this round had no such thing" and not just "plain".
   */
  roundType?: SettledRoundType
  /** The round-type price that paid. Undefined for PLAIN, which is never bettable. */
  roundTypePaid?: number
  /** The exact price that paid. HHH/MMM are settled but never bettable, so
   *  this is undefined for those two rounds even though the pattern exists. */
  exactPaid?: number
  /** True when the round was a Lightning round, boosted options priced or not. */
  lightning: boolean
  /**
   * The boosted options, in the order the race row lists them. EMPTY on a
   * non-Lightning round, and also on a Lightning round whose boosted marble the
   * widget cannot price (an id outside the twelve, or an option this round did
   * not price) — the card then drops the lightning tile rather than inventing a
   * number, which is the "if the data is genuinely absent" arm of the spec.
   */
  boosts: BirdieRoundBoost[]
  fairness?: BirdieRoundFairness
  /** `race_settled`'s own timestamp, ms. The reveal hold is measured from it. */
  settledAtMs: number
}

/** The reveal exactly as the settled event declares it — snake_case, every key
 *  optional, shared with the other fairness families. */
type FairnessWire = NonNullable<RaceSettledEvent['payload']['fairness']>

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

const asStringList = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined
  const out: string[] = []
  for (const entry of value) if (typeof entry === 'string') out.push(entry)
  return out.length > 0 ? out : undefined
}

const asNumberList = (value: unknown): number[] | undefined => {
  if (!Array.isArray(value)) return undefined
  const out: number[] = []
  for (const entry of value)
    if (typeof entry === 'number' && Number.isFinite(entry)) out.push(entry)
  return out.length > 0 ? out : undefined
}

/** A name → whole-ppm record, all or nothing: a weight table with one bad entry
 *  is not a table, and passing a partial one to the verifier would recompute
 *  the draw against the wrong total. */
const asPpmRecord = (value: unknown): Record<string, number> | undefined => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined
  const out: Record<string, number> = {}
  for (const [name, ppm] of Object.entries(value as Record<string, unknown>)) {
    if (typeof ppm !== 'number' || !Number.isInteger(ppm) || ppm < 0) return undefined
    out[name] = ppm
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/** The verifier's canonical order for the round-type weights; a record has none. */
const ROUND_TYPE_ORDER = ['none', 'frost', 'fire'] as const

/**
 * Pull the Birdie reveal out of a settle payload.
 *
 * `commitment` is the `race_ready` hash, which is what a player could have
 * recorded before betting — it wins over the reveal's own copy of the same
 * value. Returns undefined when there is nothing at all to show, so the sheet
 * can drop the whole section rather than render a block of dashes.
 *
 * Takes `unknown`, not the settled payload: `isRaceSettledEvent`'s schema does
 * not describe `fairness` (its payload object is neither strict nor
 * passthrough — it strips what it does not name, and the guard only reads the
 * boolean), so nothing upstream has checked these values. The cast below buys
 * KEY names from the type; the `as*` helpers still buy the values, and the
 * "drops values of the wrong type" test is the proof that they must.
 */
export function readBirdieFairness(
  payload: unknown,
  commitment?: string
): BirdieRoundFairness | undefined {
  const raw: unknown = (payload as RaceSettledEvent['payload'] | undefined)?.fairness
  if (raw === null || typeof raw !== 'object') {
    return commitment === undefined ? undefined : { serverSeedHash: commitment }
  }
  const f = raw as FairnessWire
  const view: BirdieRoundFairness = {}
  const set = <K extends keyof BirdieRoundFairness>(
    key: K,
    value: BirdieRoundFairness[K] | undefined
  ): void => {
    if (value !== undefined) view[key] = value
  }
  set('game', asString(f.game))
  set('serverSeedHash', commitment ?? asString(f.server_seed_hash))
  set('serverSeed', asString(f.server_seed))
  set('chainRootHash', asString(f.chain_root_hash))
  set('chainIndex', asNumber(f.chain_index))
  set('draw', asString(f.draw))
  set('marbles', asStringList(f.marbles))
  set('order', asStringList(f.order))
  set('k', asNumber(f.k))
  set('index', asNumber(f.index))
  set('weightsPpm', asNumberList(f.weights_ppm))
  set('cardDraw', asString(f.card_draw))
  set('cardIndex', asNumber(f.card_index))
  set('catalogueVersion', asNumber(f.catalogue_version))
  set('catalogueHash', asString(f.catalogue_hash))
  set('catalogueSize', asNumber(f.catalogue_size))
  set('makeRatePpm', asNumber(f.make_rate_ppm))
  set('rtpPpm', asNumber(f.rtp_ppm))
  set('paytableHash', asString(f.paytable_hash))
  set('clipId', asString(f.clip_id))
  set('clipSha256', asString(f.clip_sha256))
  set('roundType', asString(f.round_type))
  set('roundTypeDraw', asString(f.round_type_draw))
  set('roundTypesPpm', asPpmRecord(f.round_types_ppm))
  return Object.keys(view).length > 0 ? view : undefined
}

/** The 12 marble rows carry `color` = the canonical option key. Inverse of
 *  `roundOdds.patternIdByKey`, which the pricing path needs the other way up. */
const keyByMarbleId = (
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const marble of marbles) out[marble.uuid] = marble.color.toUpperCase()
  return out
}

const settledEventOf = (race: Race) => race.raceEvents.find(isRaceSettledEvent)

/**
 * One row's worth of a settled round, or undefined.
 *
 * Undefined for a round that has not settled and for one that carries no
 * `race_ready` odds: without the board there is no make chance and no price,
 * which is two of the row's three columns — a half-row would be worse than no
 * row. Prices are the round's OWN committed multipliers, never today's.
 */
export function birdiePastRound(
  race: Race,
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
): BirdiePastRound | undefined {
  const settled = settledEventOf(race)
  if (settled === undefined) return undefined
  const odds = selectRoundOdds(race)
  if (odds === undefined) return undefined

  const settledAtMs = new Date(settled.eventTimestamp).getTime()
  if (Number.isNaN(settledAtMs)) return undefined

  const keyById = keyByMarbleId(marbles)
  const countId = countWinnerId(settled.payload)
  const exactId = exactWinnerId(settled.payload, marbles)
  const patternKey = patternFromSettled(settled.payload, marbles)
  const countKey = countId === undefined ? undefined : keyById[countId]
  const countPaid = countId === undefined ? undefined : odds.multipliers[countId]
  const exactPaid = exactId === undefined ? undefined : odds.multipliers[exactId]
  const roundType = roundTypeFromSettled(settled.payload, marbles)
  // PLAIN is settled but never priced, so it has no paid figure — same shape as HHH/MMM on exact.
  const roundTypeId =
    roundType === undefined || roundType === 'PLAIN'
      ? undefined
      : marbles.find((m) => m.color.toUpperCase() === roundType)?.uuid
  const roundTypePaid = roundTypeId === undefined ? undefined : odds.multipliers[roundTypeId]

  const { golfer, difficulty, makeRatePpm } = odds.card
  // The revealed green first, the round's own committed card second (E2): a
  // round whose `race_started` never landed — or landed without a venue — still
  // has its putt length on the card, and the row would otherwise drop it.
  const distanceM = selectRoundLocation(race)?.venue?.distance_m ?? odds.card.distanceM
  const commitment = race.raceEvents.find((event) => event.eventType === 'race_ready') as
    | { payload?: { serverSeedHash?: string } }
    | undefined
  const fairness = readBirdieFairness(settled.payload, commitment?.payload?.serverSeedHash)
  // The base RTP the board was priced at, for the verifier's paytable check: the reveal's own
  // rtp_ppm when the engine published one, else the odds snapshot's (every race_ready carries it —
  // rounds before 2026-09-21 were 950000, and the verifier would otherwise assume today's 940000).
  if (fairness !== undefined && fairness.rtpPpm === undefined) fairness.rtpPpm = odds.rtpPpm

  // The Lightning boost, priced both ways (B19). A settled round is past its
  // reveal, so there is nothing to withhold — the live board only gates the
  // boost behind `race_started` because showing it while betting is open would
  // hand out the round's own tell.
  const boostPercent = race.lightningBoostPercent ?? 0
  const boosts: BirdieRoundBoost[] = []
  if (race.isLightningRound && boostPercent > 0) {
    for (const marbleId of race.lightningBoostedItems ?? []) {
      const key = keyById[marbleId]
      const base = odds.multipliers[marbleId]
      if (key === undefined || base === undefined) continue
      boosts.push({ key, base, paid: base * (1 + boostPercent / 100) })
    }
  }

  return {
    roundId: race.uuid,
    roundNumber: race.round,
    golfer: {
      name: golfer.name,
      ...(golfer.headshotUrl === undefined ? {} : { headshotUrl: golfer.headshotUrl }),
      initials: golferInitials(golfer.name)
    },
    makeRatePct: Math.round((makeRatePpm / 1_000_000) * 100),
    age: golfer.age,
    ...(golfer.handicap === undefined ? {} : { handicap: golfer.handicap }),
    ...(distanceM === undefined ? {} : { distanceM }),
    levelStars: LEVEL_STARS[golfer.level] ?? 0,
    difficultyStars: difficulty,
    marks: patternKey === undefined ? [] : circlesFor(patternKey),
    ...(patternKey === undefined ? {} : { patternKey }),
    ...(countKey === undefined ? {} : { countKey }),
    ...(countPaid === undefined ? {} : { countPaid }),
    ...(exactPaid === undefined ? {} : { exactPaid }),
    ...(roundType === undefined ? {} : { roundType }),
    ...(roundTypePaid === undefined ? {} : { roundTypePaid }),
    lightning: race.isLightningRound,
    boosts,
    ...(fairness === undefined ? {} : { fairness }),
    settledAtMs
  }
}

export interface BirdiePastRoundsInput {
  races: readonly Race[]
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
  nowMs: number
  limit?: number
}

/**
 * The strip, newest first.
 *
 * ANTI-SPOILER (the rule the whole board is built on): a round is withheld
 * until its own reveal hold has elapsed, exactly as the capsule withholds the
 * final score. `race_settled` lands on the EVENT clock, which leads the
 * viewer's frames by their playout buffer — a row that appeared the moment the
 * event arrived would print the third putt's result beside a clip still showing
 * the ball in the air. Same predicate, same constant, one behaviour
 * (`boardPhase.puttResultVisible`).
 *
 * Order is the store's own: `races.allIds` is unshifted, so the array is
 * already newest-first and `races[0]` is the round in play — the same
 * assumption the shared results strip makes.
 */
export function birdiePastRounds(input: BirdiePastRoundsInput): BirdiePastRound[] {
  const limit = input.limit ?? BIRDIE_PAST_ROUNDS_MAX
  const out: BirdiePastRound[] = []
  for (const race of input.races) {
    if (out.length >= limit) break
    const round = birdiePastRound(race, input.marbles)
    if (round === undefined) continue
    if (!puttResultVisible({ settledAt: round.settledAtMs, now: input.nowMs })) continue
    out.push(round)
  }
  return out
}

/**
 * When the newest settled round becomes showable, ms — or undefined when no
 * round has settled at all.
 *
 * Deliberately independent of "now" so the host can arm ONE timer on it and
 * re-arm only when a new round settles: a value derived from the current clock
 * would change on every render and re-arm the timeout forever. Already-past
 * instants are the caller's problem (and are cheap — the guard is a
 * comparison).
 */
export function latestRevealAtMs(races: readonly Race[]): number | undefined {
  let latest: number | undefined
  for (const race of races) {
    const settled = settledEventOf(race)
    if (settled === undefined) continue
    const at = new Date(settled.eventTimestamp).getTime()
    if (Number.isNaN(at)) continue
    if (latest === undefined || at > latest) latest = at
  }
  return latest === undefined ? undefined : latest + BIRDIE_RESULT_HOLD_MS
}

/**
 * The round's deep link into the public verifier, pre-filled.
 *
 * SHARED URL CONTRACT: the same param names the crash round modal, the public
 * verifier and operator-api's `verifyURL` already use
 * (`CrashRoundModal.tsx:345-388`) — `game` is the discriminator, `serverSeed` /
 * `observedCommitment` / `chainRootHash` / `chainIndex` are the envelope, and
 * the marble family adds `marbles` / `k` / `order` / `index` / `draw` /
 * `weightsPpm`. Birdie appends its own card-and-catalogue terms in the same
 * camelCase style.
 *
 * The public verifier recomputes a `game=birdie` round from exactly these terms
 * (155-fairness-verifier `verify.html`, merged 2026-09-17) — the card draw over
 * the catalogue, the paytable and the round-type draw. Falls back to the
 * bare tool when the seed has not been revealed, rather than opening a page
 * that can only fail.
 *
 * Takes only the round's `fairness` (B33): the shared crash round modal builds
 * the same link from `RoundFairnessInfo.birdie`, which is this very reader's
 * output, without having to fabricate a whole past-round row around it.
 */
export function verifierLink(round: Pick<BirdiePastRound, 'fairness'>): string {
  const f = round.fairness
  if (f?.serverSeed === undefined) return VERIFIER_BASE_URL
  const p = new URLSearchParams()
  p.set('game', f.game ?? 'birdie')
  p.set('serverSeed', f.serverSeed)
  const put = (key: string, value: string | number | undefined): void => {
    if (value !== undefined) p.set(key, String(value))
  }
  put('observedCommitment', f.serverSeedHash)
  put('chainRootHash', f.chainRootHash)
  put('chainIndex', f.chainIndex)
  if (f.marbles !== undefined) p.set('marbles', f.marbles.join(','))
  put('k', f.k)
  if (f.order !== undefined) p.set('order', f.order.join(','))
  put('index', f.index)
  put('draw', f.draw)
  if (f.weightsPpm !== undefined) p.set('weightsPpm', f.weightsPpm.join(','))
  put('cardDraw', f.cardDraw)
  put('cardIndex', f.cardIndex)
  put('catalogueVersion', f.catalogueVersion)
  put('catalogueHash', f.catalogueHash)
  put('catalogueSize', f.catalogueSize)
  put('makeRatePpm', f.makeRatePpm)
  put('rtpPpm', f.rtpPpm)
  put('paytableHash', f.paytableHash)
  put('clipId', f.clipId)
  put('clipSha256', f.clipSha256)
  put('roundType', f.roundType)
  put('roundTypeDraw', f.roundTypeDraw)
  if (f.roundTypesPpm !== undefined) {
    // `name:ppm` pairs in the verifier's canonical order, whatever order the
    // reveal's object decoded in; names the verifier does not know are dropped.
    const pairs = ROUND_TYPE_ORDER.filter((name) => name in f.roundTypesPpm!).map(
      (name) => `${name}:${f.roundTypesPpm![name]}`
    )
    if (pairs.length > 0) p.set('roundTypesPpm', pairs.join(','))
  }
  return `${VERIFIER_BASE_URL}verify.html?${p.toString()}`
}
